import styled, { css } from "styled-components";
import { borderRadius, rawColors } from "@magicbi/ui-library";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin, fontSizes, fontWeights } from "../../../design-system/tokens/typography";

import { STATUS_PILL_ERROR_BG } from "./StatusPill.constants";

/** GAP: 22px chip height has no spacing token. */
const CHIP_HEIGHT = "22px";

export type StatusVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral"
  | "published"
  | "selected"
  | "positive";

export type StatusPillSize = "md" | "sm" | "chip";
/* "chip" = compact tonal chip: 22px high, radius md, 11px bold uppercase, 6px dot. */
/** "soft" = light fill + matching coloured border (Figma workspace member status pills). */
export type StatusPillAppearance = "filled" | "outline" | "soft" | "dot";

/** "dot" = no fill and no border: a coloured dot and a coloured word (status lines in lists and tables). */
const dotColor = (v: StatusVariant) => {
  switch (v) {
    case "success":
    case "selected":
    case "positive":
      return rawColors.accent.green500;
    case "warning":
      return rawColors.accent.amber500;
    case "error":
      return rawColors.accent.red500;
    case "info":
    case "published":
      return rawColors.primary[500];
    default:
      return rawColors.text.mediumEmphasis;
  }
};

const variantStyles = (
  v: StatusVariant,
  appearance: StatusPillAppearance,
) => {
  switch (v) {
    case "success":
      if (appearance === "outline") return css`
        background-color: transparent;
        border: 1px solid ${rawColors.accent.green500};
        color: ${rawColors.accent.green500};
      `;
      if (appearance === "soft") return css`
        background-color: ${rawColors.accent.greenMetricBg};
        border: 1px solid ${rawColors.accent.green500};
        color: ${rawColors.accent.green500};
      `;
      return css`
        background-color: ${rawColors.accent.greenMetricBg};
        color: ${rawColors.accent.green500};
      `;
    case "warning":
      if (appearance === "soft") return css`
        background-color: ${rawColors.accent.yellowLight};
        border: 1px solid ${rawColors.accent.yellow500};
        color: ${rawColors.accent.yellow500};
      `;
      return css`
        background-color: ${rawColors.accent.yellowLight};
        color: ${rawColors.accent.yellow500};
      `;
    case "error":
      if (appearance === "soft") return css`
        background-color: ${STATUS_PILL_ERROR_BG};
        border: 1px solid ${rawColors.accent.red500};
        color: ${rawColors.accent.red500};
      `;
      return css`
        background-color: ${STATUS_PILL_ERROR_BG};
        color: ${rawColors.accent.red500};
      `;
    case "info":
      if (appearance === "soft") return css`
        background-color: ${rawColors.primary[100]};
        border: 1px solid ${rawColors.primary[500]};
        color: ${rawColors.primary[500]};
      `;
      return css`
        background-color: ${rawColors.primary[100]};
        color: ${rawColors.primary[500]};
      `;
    case "published":
      if (appearance === "soft") return css`
        background-color: ${rawColors.accent.dataStoryPurpleBg};
        border: 1px solid ${rawColors.accent.dataStoryPurple};
        color: ${rawColors.accent.dataStoryPurple};
      `;
      return css`
        background-color: ${rawColors.accent.dataStoryPurpleBg};
        color: ${rawColors.accent.dataStoryPurple};
      `;
    case "selected":
      /* Figma "Selected" workspace chip — green outline + light-green fill.
         Token gaps vs Figma (closest existing tokens used):
           bg     Figma `#EFFFF1` (chips/green-light) vs `accent.greenLight` `#DEFFED`
           border Figma `#539D5A` (chips/green)       vs `accent.green500`  `#16A34A`
           text   Figma `#16A34A`                    vs `accent.green500`  `#16A34A` exact
         Caller should pass `showDot={false}` since the Figma chip has no leading dot. */
      return css`
        background-color: ${rawColors.accent.greenLight};
        border: 1px solid ${rawColors.accent.green500};
        color: ${rawColors.accent.green500};
      `;
    case "positive":
      return css`
        background-color: ${rawColors.accent.positive.bg};
        color: ${rawColors.accent.positive.text};
        ${Dot} {
          background-color: ${rawColors.accent.positive.solid};
        }
      `;
    case "neutral":
    default:
      return css`
        background-color: ${rawColors.structural[100]};
        color: ${rawColors.text.mediumEmphasis};
      `;
  }
};

export const Pill = styled.span<{
  $variant: StatusVariant;
  $size: StatusPillSize;
  $appearance: StatusPillAppearance;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  ${({ $size }) =>
    $size === "chip"
      ? css`
          height: ${CHIP_HEIGHT};
          padding: 0 ${spacing[2]};
          border-radius: ${borderRadius.md};
          font-size: ${fontSizes["11"]};
          font-weight: ${fontWeights.bold};
          text-transform: uppercase;
        `
      : css`
          padding: ${$size === "sm" ? "3px 10px" : `4px ${spacing[3]}`};
          border-radius: 30px;
          ${$size === "sm" ? typographyMixin("overline", 3) : typographyMixin("caption", 3)}
        `}
  border: 1px solid transparent;
  ${({ $variant, $appearance }) =>
    $appearance === "dot"
      ? css`
          background-color: transparent;
          padding-left: 0;
          padding-right: 0;
          color: ${dotColor($variant)};
        `
      : variantStyles($variant, $appearance)}
`;

export const Dot = styled.span<{ $size?: StatusPillSize }>`
  display: inline-block;
  width: ${({ $size }) => ($size === "chip" ? spacing[1.5] : "8px")};
  height: ${({ $size }) => ($size === "chip" ? spacing[1.5] : "8px")};
  border-radius: 50%;
  background-color: currentColor;
`;
